import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Separator } from '@/common/ui/separator'

import type { GetInviteFriendReferredPlayersData } from '@/modules/manage-player/type'

interface ReferralCommissionModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	playerData: GetInviteFriendReferredPlayersData | null
}

const formatNumber = (value: number): string => {
	return value.toLocaleString('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

const gameTypeLabels: Record<string, string> = {
	SLOT: 'สล็อต',
	CASINO: 'คาสิโน',
	LOTTO: 'หวย',
	SPORT: 'กีฬา',
	FISHING: 'ยิงปลา',
}

export default function ReferralCommissionModal({
	open,
	onOpenChange,
	playerData,
}: ReferralCommissionModalProps) {
	if (!playerData) return null

	const {
		phone,
		totalSignupCommission,
		totalFirstDepositCommission,
		totalTurnoverCommission,
		totalReturnLossCommission,
		totalAllCommissions,
		turnoverCommissionByGameType,
		returnLossCommissionByGameType,
	} = playerData

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
				<DialogHeader>
					<DialogTitle>รายละเอียดค่าคอมมิชชั่น</DialogTitle>
					<p className="text-sm text-muted-foreground">สมาชิก: {phone}</p>
				</DialogHeader>

				<div className="space-y-6">
					{/* Summary Section */}
					<div className="space-y-3">
						<h3 className="text-sm font-semibold">สรุปค่าคอมมิชชั่น</h3>
						<div className="grid gap-3">
							<div className="flex items-center justify-between rounded-lg border p-3">
								<span className="text-sm text-muted-foreground">
									ค่าคอมมิชชั่นสมัครสมาชิก
								</span>
								<span className="font-medium">
									{formatNumber(totalSignupCommission)}
								</span>
							</div>
							<div className="flex items-center justify-between rounded-lg border p-3">
								<span className="text-sm text-muted-foreground">
									ค่าคอมมิชชั่นฝากครั้งแรก
								</span>
								<span className="font-medium">
									{formatNumber(totalFirstDepositCommission)}
								</span>
							</div>
							<div className="flex items-center justify-between rounded-lg border p-3">
								<span className="text-sm text-muted-foreground">
									ค่าคอมมิชชั่นยอดเทิร์น
								</span>
								<span className="font-medium">
									{formatNumber(totalTurnoverCommission)}
								</span>
							</div>
							<div className="flex items-center justify-between rounded-lg border p-3">
								<span className="text-sm text-muted-foreground">
									ค่าคอมมิชชั่นคืนยอดเสียชวนเพื่อน
								</span>
								<span className="font-medium">
									{formatNumber(totalReturnLossCommission)}
								</span>
							</div>
						</div>
					</div>

					<Separator />

					{/* Turnover Commission by Game Type */}
					{turnoverCommissionByGameType &&
						turnoverCommissionByGameType.length > 0 && (
							<div className="space-y-3">
								<h3 className="text-sm font-semibold">
									รายละเอียดค่าคอมมิชชั่นยอดเทิร์นตามประเภทเกม
								</h3>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>ประเภทเกม</TableHead>
											<TableHead className="text-right">
												ค่าคอมมิชชั่น
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{turnoverCommissionByGameType.map((item, index) => (
											<TableRow key={index}>
												<TableCell>
													{gameTypeLabels[item.gameType] || item.gameType}
												</TableCell>
												<TableCell className="text-right font-medium">
													{formatNumber(item.commissionAmount)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}

					{/* Return Loss Commission by Game Type */}
					{returnLossCommissionByGameType &&
						returnLossCommissionByGameType.length > 0 && (
							<div className="space-y-3">
								<h3 className="text-sm font-semibold">
									รายละเอียดค่าคอมมิชชั่นคืนยอดเสียชวนเพื่อนตามประเภทเกม
								</h3>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>ประเภทเกม</TableHead>
											<TableHead className="text-right">
												ค่าคอมมิชชั่น
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{returnLossCommissionByGameType.map((item, index) => (
											<TableRow key={index}>
												<TableCell>
													{gameTypeLabels[item.gameType] || item.gameType}
												</TableCell>
												<TableCell className="text-right font-medium">
													{formatNumber(item.commissionAmount)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}

					<Separator />

					{/* Total Section */}
					<div className="flex items-center justify-between rounded-lg bg-primary/10 p-4">
						<span className="font-semibold">ค่าคอมมิชชั่นทั้งหมด</span>
						<span className="text-lg font-bold">
							{formatNumber(totalAllCommissions)}
						</span>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
